import DOMPurify, { type Config } from "dompurify";

/**
 * Sanitise LLM-generated table HTML before it is handed to
 * `dangerouslySetInnerHTML` (issue #584).
 *
 * The `html` field on a table object is raw model output derived from a
 * user-uploaded PDF: the extraction pipeline only pulls the outermost
 * `<table>…</table>` out of the model's reply, so anything nested inside it —
 * `<script>`, `<img onerror=…>`, `javascript:` URLs — reaches the browser
 * untouched. Rendered unsanitised, a crafted lab-book page could exfiltrate the
 * JWT in `localStorage` from whoever opens the document.
 *
 * The allowlist below is deliberately narrower than "strip event handlers":
 * chemistry tables need no links, images or inline CSS, so those tags and
 * attributes are simply not representable in the output. `sup`/`sub` are kept
 * because formulae and charges rely on them.
 *
 * Widening this config is a security change — see
 * `.claude/claude-security-guidance.md`.
 */
const TABLE_HTML_CONFIG: Config = Object.freeze({
  ALLOWED_TAGS: [
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "td",
    "th",
    "caption",
    "colgroup",
    "col",
    "b",
    "i",
    "em",
    "strong",
    "sup",
    "sub",
    "br",
    "p",
  ],
  // No `style` (blocks CSS-based exfiltration and UI redressing), no `href`
  // or `src` (no network egress), no `class`/`id` (cannot target app styles).
  ALLOWED_ATTR: ["colspan", "rowspan"],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
});

/**
 * Returns table markup safe to inject, or `""` for missing input — callers
 * guard on falsiness to show their "no content extracted" placeholder.
 */
export function sanitizeTableHtml(html: string | null | undefined): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, TABLE_HTML_CONFIG);
}
